import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * The app's modal frame (as in the datasets modals): title bar, body, Escape closes.
 * Mousedown is left to bubble so dropdowns inside close on an outside click, and an
 * Escape a dropdown already handled (defaultPrevented) closes only that dropdown.
 */
export default function Modal({ title, onClose, children, width = 'max-w-md', footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[color:var(--surface-overlay)] p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div
        className={`flex max-h-[90vh] w-full ${width} flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-2xl`}
        role="dialog"
        aria-label={title}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <div className="text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] hover:bg-surface-hover">
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[color:var(--border-subtle)] px-4 py-3">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export const buttonClass = 'inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50';
export const primaryButtonClass = 'inline-flex h-8 items-center gap-1.5 rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90 disabled:opacity-50 whitespace-nowrap';
export const inputClass = 'h-8 w-full rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--brand-primary)]';
export const labelClass = 'mb-1 block text-[11px] font-medium text-[color:var(--text-muted)]';
